import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import { useModalContext } from '../../context/Modal';
import {
  LIST_QR_AUTHS,
  REQUEST_QR_AUTH,
  DELETE_QR_AUTH,
  ON_CLAIM_QR,
} from '../../http/apollo-queries';
import { Loading, Button, Icon } from '../UI';
import { Modal } from '../Modal';
import { detectDevice } from './helpers';
import DisconnectDevice from './DisconnectDevice';
import { formatDateTime, truncateString } from '../../helpers/functions';

export type QRAuth = {
  createdAt: string;
  device: string;
  deviceName: string;
  email: string;
  id: string;
  owner: string;
  qr: string;
  status: string;
  updatedAt: string;
};

const Devices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setElement } = useModalContext();

  const [claimId, setClaimId] = useState('');

  const {
    data: qrAuths,
    loading: qrsLoading,
    refetch,
    subscribeToMore,
  } = useQuery<{
    listQRAuths: {
      items: QRAuth[];
      nextToken?: string | null;
    };
    onClaimQR: QRAuth;
  }>(LIST_QR_AUTHS, { fetchPolicy: 'network-only' });

  subscribeToMore({
    document: ON_CLAIM_QR,
    variables: {
      id: claimId,
    },
    updateQuery: (prev, { subscriptionData }) => {
      const newQRAuth = subscriptionData.data.onClaimQR;
      if (!newQRAuth) {
        return prev;
      }

      const exists = prev.listQRAuths.items.find(
        ({ id }) => id === newQRAuth.id
      );
      if (exists) {
        return prev;
      }

      return {
        ...prev,
        listQRAuths: {
          items: [newQRAuth, ...(prev?.listQRAuths?.items || [])],
          nextToken: prev?.listQRAuths?.nextToken || null,
        },
      };
    },
  });

  const [requestQr, { loading: qrLoading, data: qrData }] =
    useMutation(REQUEST_QR_AUTH);

  const [deleteQr, { loading: deleteQrLoading }] = useMutation(DELETE_QR_AUTH);

  const handleCloseModal = () => {
    navigate('/');
  };

  useEffect(() => {
    requestQr().then(({ data }) => {
      setClaimId(data.requestQRAuth.id);
    });
  }, []);

  const handleOpenModal = (connection: any) => {
    setElement(
      <DisconnectDevice
        connection={connection}
        onSave={() =>
          deleteQr({
            variables: {
              id: connection.id,
            },
          }).then(() => refetch())
        }
      />
    );
  };

  const ModalElement = (
    <div className="flex flex-col w-full h-full">
      <Icon
        className="cursor-pointer absolute right-4 top-6"
        svgName={'close'}
        onClick={handleCloseModal}
      />
      <div className="text-grey-50 text-center mb-2">
        {t('modals.devices.subtitle')}
      </div>
      <div className="w-1/2 mx-auto mb-8">
        <Loading className="m-auto" status={qrLoading} />

        {qrData?.requestQRAuth?.qr && (
          <img
            alt={qrData.requestQRAuth.id}
            className="w-full"
            src={qrData.requestQRAuth.qr}
          />
        )}
      </div>
      {qrsLoading || deleteQrLoading ? (
        <Loading className="m-auto" status={true} />
      ) : (
        !!qrAuths?.listQRAuths?.items.length && (
          <div className="border-t-1 border-grey-10 dark:border-grey-70 mx-8">
            <p className="text-grey-50 uppercase my-4">
              {t('modals.devices.connectedDevices')}
            </p>
            <div className="my-8 flex flex-col gap-8 overflow-y-auto h-48">
              {qrAuths.listQRAuths.items.map((qrConnection) => {
                const {
                  device: userAgent,
                  deviceName,
                  updatedAt,
                  id,
                } = qrConnection;

                const device = detectDevice(userAgent);

                return (
                  <div className="flex items-center justify-between" key={id}>
                    <div className="flex gap-4">
                      <div className="flex justify-center items-center rounded-full bg-grey-5 w-12 h-12">
                        <span className="material-icons select-none text-grey-50 text-2xl">
                          {device || 'device_unknown'}
                        </span>
                      </div>
                      <div>
                        <p>{truncateString(deviceName, 30)}</p>
                        <p className="text-grey-50">
                          {`${t('modals.devices.lastActive')} ${formatDateTime(
                            updatedAt
                          )}`}
                        </p>
                      </div>
                    </div>
                    <div>
                      <Button
                        buttonClass="bg-none border border-grey-30"
                        type="button"
                        onClick={() => handleOpenModal(qrConnection)}
                      >
                        {t('modals.devices.disconnect')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}
    </div>
  );

  return (
    <Modal
      z={50}
      padding={'base'}
      title={t('modals.devices.title')}
      height="auto"
      element={ModalElement}
      isOpen={true}
    />
  );
};

export default Devices;
